import React from "react";
import { motion } from "framer-motion";

import TextArea from "./ui/TextArea";
import Card from "./ui/Card";
import LessonDateCard from "./LessonDateCard";
import DateSelectionModal from "./DateSelectionModal";
import ConfirmationModal from "./ConfirmationModal";
import type { LessonDate, Participant } from "../../types/booking";
import type { FieldErrors } from "react-hook-form";

interface LessonRequestFormProps {
  lessonDates: LessonDate[];
  onAddParticipant: (lessonDateId: string, sharedInstructor?: 0 | 1) => void;
  onRemoveParticipant: (lessonDateId: string, participantId: string) => void;
  onAddLessonDate: (selectedDate?: Date) => void;
  onRemoveLessonDate: (lessonDateId: string) => void;
  onUpdateLessonDate: (lessonDateId: string, date: Date | null) => void;
  onUpdateParticipant: (
    lessonDateId: string,
    participantId: string,
    updates: Partial<Participant>,
  ) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any; // Using any to avoid complex type drilling for now, or import UseFormRegister
  errors?: FieldErrors<{ lessonDates: LessonDate[] }>["lessonDates"];
}

const LessonRequestForm: React.FC<LessonRequestFormProps> = ({
  lessonDates,
  onAddParticipant,
  onRemoveParticipant,
  onAddLessonDate,
  onRemoveLessonDate,
  onUpdateLessonDate,
  onUpdateParticipant,
  register,
  errors,
}) => {
  const [showDateModal, setShowDateModal] = React.useState(false);
  const [confirmModal, setConfirmModal] = React.useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const handleAddLessonDate = () => {
    setShowDateModal(true);
  };

  const handleDateConfirm = (selectedDate: Date) => {
    onAddLessonDate(selectedDate);
  };

  const handleRemoveLessonDate = (lessonDateId: string) => {
    setConfirmModal({
      isOpen: true,
      title: "Remove Lesson Date",
      message:
        "Are you sure you want to remove this lesson date? All participants for this date will be removed.",
      onConfirm: () => onRemoveLessonDate(lessonDateId),
    });
  };

  const handleRemoveParticipant = (
    lessonDateId: string,
    participantId: string,
  ) => {
    setConfirmModal({
      isOpen: true,
      title: "Remove Participant",
      message: "Are you sure you want to remove this participant?",
      onConfirm: () => onRemoveParticipant(lessonDateId, participantId),
    });
  };

  const getSuggestedDate = () => {
    if (lessonDates.length > 0) {
      const lastDate = lessonDates[lessonDates.length - 1];
      const baseDate =
        lastDate?.date && !Number.isNaN(lastDate.date.getTime())
          ? new Date(lastDate.date)
          : new Date();
      const suggestedDate = new Date(baseDate);
      suggestedDate.setDate(suggestedDate.getDate() + 1);
      suggestedDate.setHours(0, 0, 0, 0);
      return suggestedDate;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };
  const excludedDates = React.useMemo(() => {
    return lessonDates
      .map((ld) => ld.date)
      .filter(
        (date): date is Date =>
          date instanceof Date && !Number.isNaN(date.getTime()),
      )
      .map((date) => {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
      });
  }, [lessonDates]);
  return (
    <>
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Lesson Request Details
          </h2>
          <div className="bg-blue-50 p-6 rounded-lg flex flex-col lg:flex-row lg:items-center">
            <h3
              className="  mb-3 
          text-[36px] lg:text-[30px] text-blue-800 font-[700] max-w-[18ch] text-left
          leading-[1.2]"
            >
              How to fill out your lesson request
            </h3>
            <ol
              className="text-sm border-l-2 flex flex-col gap-2 border-blue-800 pl-4 
          text-gray-700 space-y-2 text-left text-[16px]"
            >
              <li>
                <span className="font-semibold text-blue-800">Step 1:</span>{" "}
                Select the date of your first lesson
              </li>
              <li>
                <span className="font-semibold text-blue-800">Step 2:</span>{" "}
                Enter participant details for this date
              </li>
              <li>
                <span className="font-semibold text-blue-800">Step 3:</span> Add
                more participants or lesson dates as needed
              </li>
              <li>
                <span className="font-semibold text-blue-800">Step 4:</span>{" "}
                Review and submit your booking
              </li>
            </ol>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mb-8"
        >
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Need help determining your skill level?
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://www.hakubasnowsports.com/ski-lesson-levels"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 w-full sm:w-auto justify-center"
              >
                Ski Levels Explanation
              </a>
              <a
                href="https://www.hakubasnowsports.com/snowboard-lesson-levels"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 w-full sm:w-auto justify-center"
              >
                Snowboard Levels Explanation
              </a>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6 mb-8"
        >
          <div id="comments-section" className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h3 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              Comments
            </h3>
            <TextArea
              {...register("bookingPerson.comments")}
              placeholder="Add any Comments here regarding accommodation, instructor requests, special requirements or anything else that maybe pertinent to your lessons."
              rows={3}
              maxLength={1000}
              helpText="Maximum 1000 characters"
            />
          </div>
        </motion.div>

        <div className="space-y-6">
          {lessonDates.map((lessonDate, index) => {
            // Calculate exclude dates for this lesson date card
            // Exclude all other lesson dates except the current one
            const excludeDates = lessonDates
              .filter((_, i) => i !== index)
              .map((ld) => ld.date)
              .filter(
                (date): date is Date =>
                  date instanceof Date && !Number.isNaN(date.getTime()),
              )
              .map((date) => {
                const normalized = new Date(date);
                normalized.setHours(0, 0, 0, 0);
                return normalized;
              });

            return (
              <LessonDateCard
                key={lessonDate.id}
                lessonDate={lessonDate}
                index={index}
                canRemove={lessonDates.length > 1}
                onUpdate={(id, updates) => {
                  if ("date" in updates) {
                    onUpdateLessonDate(id, updates.date ?? null);
                  }
                }}
                onRemove={handleRemoveLessonDate}
                onAddParticipant={onAddParticipant}
                onRemoveParticipant={handleRemoveParticipant}
                onUpdateParticipant={onUpdateParticipant}
                onAddLessonDate={handleAddLessonDate}
                isLast={index === lessonDates.length - 1}
                excludeDates={excludeDates}
                errors={
                  errors?.[index] as
                    | {
                        date?: { message?: string };
                        participants?: Array<{
                          name?: { message?: string };
                          age?: { message?: string };
                          activity?: { message?: string };
                          timeSlot?: { message?: string };
                          skillLevel?: { message?: string };
                          lessonType?: { message?: string };
                        }>;
                      }
                    | undefined
                }
              />
            );
          })}
        </div>
      </div>

      <DateSelectionModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        onConfirm={handleDateConfirm}
        suggestedDate={getSuggestedDate()}
        excludeDates={excludedDates}
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type="danger"
        confirmText="Remove"
      />
    </>
  );
};

export default LessonRequestForm;
